import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Container,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { GameMode, colors } from "../types";
import StartGameButtons from "../components/StartGameButtons";

type GameStartParams = {
  mode: GameMode;
  timed?: boolean;
  worldId?: number;
};

type TitlePageProps = {
  setGameMode: (gameMode: GameMode) => void;
  setUnlockedStageTypes: (unlockedStageTypes: string[]) => void;
  setLevelTimer: (levelTimer: number) => void;
  beginNewPuzzle: (mode?: GameMode, worldId?: number) => void;
  selectedWorldId: number;
  setSelectedWorldId: (worldId: number) => void;
};

const ColorCircle: React.FC<{ color: string; size: string; delay: number }> = ({
  color,
  size,
  delay,
}) => (
  <Box
    w={size}
    h={size}
    borderRadius="full"
    backgroundColor={color}
    boxShadow={`0 0 20px ${color}`}
    animation={`pulse 2s ease-in-out ${delay}s infinite`}
  />
);

const TitlePage: React.FC<TitlePageProps> = ({
  setGameMode,
  setUnlockedStageTypes,
  setLevelTimer,
  beginNewPuzzle,
  selectedWorldId,
  setSelectedWorldId,
}) => {
  const navigate = useNavigate();
  const [showWorldSelect, setShowWorldSelect] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const circleSize = useBreakpointValue({ base: "12px", md: "16px" }) || "12px";

  const handleStartGame = (params?: GameStartParams) => {
    const searchParams = new URLSearchParams();
    if (params) {
      searchParams.set("mode", params.mode);
      if (params.timed) {
        searchParams.set("timed", "true");
      }
      if (params.worldId !== undefined) {
        searchParams.set("worldId", params.worldId.toString());
      }
    }
    const queryString = searchParams.toString();
    navigate(queryString ? `/game?${queryString}` : "/game");
  };

  const titleColors = colors.slice(0, 7);

  return (
    <Box
      minH="100vh"
      w="100vw"
      backgroundColor="black"
      overflow="auto"
      py={{ base: 6, md: 10 }}
      px={{ base: 4, md: 8 }}
    >
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.8; }
            50% { transform: scale(1.2); opacity: 1; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <Container maxW="container.lg" h="100%">
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH={{ base: "auto", md: "90vh" }}
          gap={{ base: 6, md: 10 }}
        >
          {/* Title Section - Hidden when world select is shown */}
          {!showWorldSelect && (
            <VStack spacing={{ base: 3, md: 5 }} animation="fadeIn 0.6s ease-out">
              {/* Decorative circles */}
              <HStack spacing={{ base: 2, md: 3 }} mb={{ base: 2, md: 4 }}>
                {titleColors.map((color, i) => (
                  <ColorCircle
                    key={color}
                    color={color}
                    size={circleSize}
                    delay={i * 0.2}
                  />
                ))}
              </HStack>

              <Heading
                as="h1"
                fontSize={{ base: "4xl", sm: "5xl", md: "6xl", lg: "7xl" }}
                fontWeight="bold"
                color="white"
                textAlign="center"
                letterSpacing="tight"
                textShadow="0 0 40px rgba(255,255,255,0.3)"
              >
                Color Connect
              </Heading>

              <Text
                fontSize={{ base: "md", md: "xl" }}
                color="gray.400"
                textAlign="center"
                maxW="600px"
                px={4}
              >
                Connect matching circles by drawing paths between them
              </Text>
            </VStack>
          )}

          {/* Game Modes Section */}
          <Box
            w="100%"
            maxW={{ base: "100%", md: "600px" }}
            bg="whiteAlpha.50"
            borderRadius="2xl"
            p={{ base: 4, md: 8 }}
            backdropFilter="blur(10px)"
            animation={showWorldSelect ? undefined : "fadeIn 0.6s ease-out 0.2s both"}
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              {!showWorldSelect && (
                <Text
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="semibold"
                  color="white"
                  textAlign="center"
                >
                  Select Game Mode
                </Text>
              )}

              <StartGameButtons
                onStartGame={handleStartGame}
                setGameMode={setGameMode}
                setUnlockedStageTypes={setUnlockedStageTypes}
                setLevelTimer={setLevelTimer}
                beginNewPuzzle={beginNewPuzzle}
                selectedWorldId={selectedWorldId}
                setSelectedWorldId={setSelectedWorldId}
                showWorldSelect={showWorldSelect}
                setShowWorldSelect={setShowWorldSelect}
              />
            </VStack>
          </Box>

          {/* Instructions - Hidden when world select or on very small screens */}
          {!showWorldSelect && (
            <Box
              display={{ base: "none", sm: "block" }}
              animation="fadeIn 0.6s ease-out 0.4s both"
            >
              <HStack
                spacing={{ base: 4, md: 8 }}
                color="gray.500"
                fontSize={{ base: "xs", md: "sm" }}
                flexWrap="wrap"
                justify="center"
              >
                <HStack>
                  <Box w="8px" h="8px" borderRadius="full" bg="blue.500" />
                  <Text>Draw paths</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" borderRadius="full" bg="green.500" />
                  <Text>Match colors</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" borderRadius="full" bg="purple.500" />
                  <Text>Fill the grid</Text>
                </HStack>
              </HStack>
            </Box>
          )}

          {/* Mobile tip - Hidden when world select */}
          {!showWorldSelect && isMobile && (
            <Text
              fontSize="xs"
              color="gray.600"
              textAlign="center"
              animation="fadeIn 0.6s ease-out 0.6s both"
            >
              Use your finger to draw paths
            </Text>
          )}
        </Flex>
      </Container>
    </Box>
  );
};

export default TitlePage;
